import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  LinearProgress,
  Alert,
  Collapse,
  FormControlLabel,
  Checkbox,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  RadioButtonUnchecked as PendingIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  Description as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PlayArrow as GenerateIcon,
  ArrowBack as ArrowBackIcon,
  Article as ArticleIcon,
  DataObject as DataObjectIcon,
  TextFields as TextFieldsIcon,
  FormatPaint as FormatPaintIcon,
  TaskAlt as TaskAltIcon,
  Support as SupportIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';
import { useNavigate } from 'react-router-dom';

interface GenerateStepProps {
  deliverableId: number;
  templateId: string;
  selectedSections: string[];
  onGenerationComplete: (documentId: string) => void;
}

// Generation stages for progress tracking
const GENERATION_STAGES = [
  { id: 'loading_template', name: 'Loading template', icon: <ArticleIcon /> },
  { id: 'processing_assets', name: 'Processing assets', icon: <DataObjectIcon /> },
  { id: 'generating_content', name: 'Generating content', icon: <TextFieldsIcon /> },
  { id: 'applying_formatting', name: 'Applying formatting', icon: <FormatPaintIcon /> },
  { id: 'finalizing_document', name: 'Finalizing document', icon: <TaskAltIcon /> },
] as const;

type GenerationStageId = typeof GENERATION_STAGES[number]['id'];

interface OperationStatus {
  operationId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  currentStage: GenerationStageId | null;
  documentId?: string;
  documentName?: string;
  documentSize?: number;
  documentVersion?: string;
  error?: string;
  errorDetails?: string;
  startedAt?: string;
  completedAt?: string;
}

interface PreGenerationSummary {
  templateName: string;
  sectionCount: number;
  mappedAssetCount: number;
}

interface GenerationOptions {
  generateAsDraft: boolean;
  includeTableOfContents: boolean;
}

type ViewState = 'pre-generation' | 'generating' | 'success' | 'error';

export const GenerateStep: React.FC<GenerateStepProps> = ({
  deliverableId,
  templateId,
  selectedSections,
  onGenerationComplete,
}) => {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  // State
  const [viewState, setViewState] = useState<ViewState>('pre-generation');
  const [operationId, setOperationId] = useState<string | null>(null);
  const [operationStatus, setOperationStatus] = useState<OperationStatus | null>(null);
  const [options, setOptions] = useState<GenerationOptions>({
    generateAsDraft: false,
    includeTableOfContents: true,
  });
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Refs for polling and timing
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const elapsedTimeIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);

  // Fetch pre-generation summary
  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['deliverable-summary', deliverableId, templateId],
    queryFn: async () => {
      const response = await authenticatedFetch(
        `/api/deliverables/${deliverableId}/summary?templateId=${templateId}`
      );
      if (!response?.ok) {
        // Return default values if endpoint doesn't exist yet
        return {
          success: true,
          data: {
            templateName: 'Template',
            sectionCount: selectedSections.length,
            mappedAssetCount: 0,
          },
        };
      }
      return await response.json();
    },
    enabled: viewState === 'pre-generation',
  });

  const summary: PreGenerationSummary = summaryData?.data || {
    templateName: 'Template',
    sectionCount: selectedSections.length,
    mappedAssetCount: 0,
  };

  // Start generation mutation
  const startGenerationMutation = useMutation({
    mutationFn: async () => {
      const response = await authenticatedFetch(
        `/api/deliverables/${deliverableId}/generate`,
        {
          method: 'POST',
          body: JSON.stringify({
            templateId,
            sections: selectedSections,
            options: {
              isDraft: options.generateAsDraft,
              includeTableOfContents: options.includeTableOfContents,
            },
          }),
        }
      );

      if (!response?.ok) {
        const errorData = await response?.json().catch(() => ({}));
        throw new Error(errorData?.error || errorData?.message || 'Failed to start document generation');
      }

      return await response.json();
    },
    onSuccess: (data) => {
      if (data.operationId) {
        setOperationId(data.operationId);
        setViewState('generating');
        startTimeRef.current = Date.now();
        startPolling(data.operationId);
        startElapsedTimeTracking();
      }
    },
    onError: (error) => {
      setOperationStatus({
        operationId: '',
        status: 'failed',
        progress: 0,
        currentStage: null,
        error: error instanceof Error ? error.message : 'Failed to start generation',
      });
      setViewState('error');
    },
  });

  // Poll for operation status
  const pollOperationStatus = useCallback(async (opId: string) => {
    try {
      const response = await authenticatedFetch(
        `/api/deliverables/operations/${opId}`
      );

      if (!response?.ok) {
        const errorData = await response?.json().catch(() => ({}));
        throw new Error(errorData?.error || 'Failed to fetch operation status');
      }

      const data = await response.json();
      const status: OperationStatus = data.data || data;

      setOperationStatus(status);

      if (status.status === 'completed') {
        stopPolling();
        stopElapsedTimeTracking();
        setViewState('success');
        if (status.documentId) {
          onGenerationComplete(status.documentId);
        }
      } else if (status.status === 'failed' || status.status === 'cancelled') {
        stopPolling();
        stopElapsedTimeTracking();
        setViewState('error');
      }
    } catch (error) {
      console.error('Error polling operation status:', error);
      // Don't stop polling on transient errors
    }
  }, [authenticatedFetch, onGenerationComplete]);

  // Start polling interval
  const startPolling = useCallback((opId: string) => {
    // Initial poll
    pollOperationStatus(opId);

    // Set up interval for subsequent polls
    pollingIntervalRef.current = setInterval(() => {
      pollOperationStatus(opId);
    }, 2000); // Poll every 2 seconds
  }, [pollOperationStatus]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Start elapsed time tracking
  const startElapsedTimeTracking = useCallback(() => {
    elapsedTimeIntervalRef.current = setInterval(() => {
      if (startTimeRef.current) {
        setElapsedTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }
    }, 1000);
  }, []);

  // Stop elapsed time tracking
  const stopElapsedTimeTracking = useCallback(() => {
    if (elapsedTimeIntervalRef.current) {
      clearInterval(elapsedTimeIntervalRef.current);
      elapsedTimeIntervalRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
      stopElapsedTimeTracking();
    };
  }, [stopPolling, stopElapsedTimeTracking]);

  // Cancel generation mutation
  const cancelGenerationMutation = useMutation({
    mutationFn: async () => {
      if (!operationId) throw new Error('No operation to cancel');

      const response = await authenticatedFetch(
        `/api/deliverables/operations/${operationId}/cancel`,
        { method: 'POST' }
      );

      if (!response?.ok) {
        const errorData = await response?.json().catch(() => ({}));
        throw new Error(errorData?.error || 'Failed to cancel generation');
      }

      return await response.json();
    },
    onSuccess: () => {
      setShowCancelDialog(false);
      stopPolling();
      stopElapsedTimeTracking();
      setOperationStatus((prev) => prev ? { ...prev, status: 'cancelled' } : null);
      setViewState('error');
    },
  });

  // Download document
  const handleDownload = async () => {
    if (!operationStatus?.documentId) return;

    try {
      const response = await authenticatedFetch(
        `/api/deliverables/documents/${operationStatus.documentId}/download`
      );

      if (response?.ok) {
        const data = await response.json();
        if (data.success && data.downloadUrl) {
          // Open SAS URL in new tab to trigger download
          window.open(data.downloadUrl, '_blank');
        }
      }
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  // Reset and generate another version
  const handleGenerateAnother = () => {
    setViewState('pre-generation');
    setOperationId(null);
    setOperationStatus(null);
    setElapsedTime(0);
    startTimeRef.current = null;
    setShowErrorDetails(false);
  };

  // Format elapsed time
  const formatElapsedTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get current stage index
  const getCurrentStageIndex = (): number => {
    if (!operationStatus?.currentStage) return -1;
    return GENERATION_STAGES.findIndex((s) => s.id === operationStatus.currentStage);
  };

  // Get stage status
  const getStageStatus = (stageIndex: number): 'completed' | 'processing' | 'pending' | 'error' => {
    const currentIndex = getCurrentStageIndex();

    if (operationStatus?.status === 'completed') return 'completed';
    if (operationStatus?.status === 'failed' || operationStatus?.status === 'cancelled') {
      if (stageIndex === currentIndex) return 'error';
      if (stageIndex < currentIndex) return 'completed';
      return 'pending';
    }

    if (stageIndex < currentIndex) return 'completed';
    if (stageIndex === currentIndex) return 'processing';
    return 'pending';
  };

  // Get stage icon
  const getStageIcon = (stageIndex: number) => {
    const status = getStageStatus(stageIndex);
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'processing':
        return <CircularProgress size={24} />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <PendingIcon color="disabled" />;
    }
  };

  // Render Pre-Generation View
  const renderPreGenerationView = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Generate Document
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Review your configuration and generate the final document.
      </Typography>

      {/* Summary */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          Document Summary
        </Typography>

        {summaryLoading ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <DescriptionIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h5" color="primary">
                  {summary.templateName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Template
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h3" color="primary">
                  {summary.sectionCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sections
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h3" color="primary">
                  {summary.mappedAssetCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Mapped Assets
                </Typography>
              </Box>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Options */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          Generation Options
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={options.generateAsDraft}
              onChange={(e) => setOptions((prev) => ({ ...prev, generateAsDraft: e.target.checked }))}
            />
          }
          label={
            <Box>
              <Typography variant="body2">Generate as Draft</Typography>
              <Typography variant="caption" color="text.secondary">
                Adds a watermark to indicate this is a draft document
              </Typography>
            </Box>
          }
          sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={options.includeTableOfContents}
              onChange={(e) => setOptions((prev) => ({ ...prev, includeTableOfContents: e.target.checked }))}
            />
          }
          label={
            <Box>
              <Typography variant="body2">Include Table of Contents</Typography>
              <Typography variant="caption" color="text.secondary">
                Automatically generate a table of contents based on document sections
              </Typography>
            </Box>
          }
          sx={{ display: 'flex', alignItems: 'flex-start' }}
        />
      </Paper>

      {/* Generate Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={() => startGenerationMutation.mutate()}
          disabled={startGenerationMutation.isPending}
          startIcon={
            startGenerationMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <GenerateIcon />
            )
          }
          sx={{ px: 6, py: 1.5 }}
        >
          {startGenerationMutation.isPending ? 'Starting...' : 'Generate Document'}
        </Button>
      </Box>

      {startGenerationMutation.isError && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {startGenerationMutation.error instanceof Error
            ? startGenerationMutation.error.message
            : 'Failed to start document generation'}
        </Alert>
      )}
    </Box>
  );

  // Render Generating View
  const renderGeneratingView = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Generating Document
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Please wait while your document is being generated...
      </Typography>

      {/* Overall Progress */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Progress: {operationStatus?.progress || 0}%
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Elapsed: {formatElapsedTime(elapsedTime)}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={operationStatus?.progress || 0}
          sx={{ height: 8, borderRadius: 1, mb: 3 }}
        />

        {/* Stage Steps */}
        <List>
          {GENERATION_STAGES.map((stage, index) => (
            <ListItem key={stage.id} sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                {getStageIcon(index)}
              </ListItemIcon>
              <ListItemText
                primary={stage.name}
                primaryTypographyProps={{
                  color: getStageStatus(index) === 'processing' ? 'primary' : 'textPrimary',
                  fontWeight: getStageStatus(index) === 'processing' ? 600 : 400,
                }}
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Cancel Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={() => setShowCancelDialog(true)}
          startIcon={<CancelIcon />}
          disabled={
            cancelGenerationMutation.isPending ||
            (operationStatus?.progress || 0) > 80 // Don't allow cancel if almost done
          }
        >
          Cancel Generation
        </Button>
      </Box>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
      >
        <DialogTitle>Cancel Document Generation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel the document generation?
            Progress will be lost and you will need to start over.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelDialog(false)}>
            Continue Generating
          </Button>
          <Button
            onClick={() => cancelGenerationMutation.mutate()}
            color="error"
            disabled={cancelGenerationMutation.isPending}
          >
            {cancelGenerationMutation.isPending ? 'Cancelling...' : 'Cancel Generation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  // Render Success View
  const renderSuccessView = () => (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Document Generated Successfully!
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your document is ready for download.
        </Typography>
      </Box>

      {/* Document Details */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          Document Details
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary">Name</Typography>
            <Typography variant="body1">
              {operationStatus?.documentName || 'Generated Document'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary">Size</Typography>
            <Typography variant="body1">
              {operationStatus?.documentSize
                ? formatFileSize(operationStatus.documentSize)
                : 'Unknown'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary">Version</Typography>
            <Typography variant="body1">
              {operationStatus?.documentVersion || '1.0'}
            </Typography>
          </Grid>
        </Grid>

        {elapsedTime > 0 && (
          <Box sx={{ mt: 2 }}>
            <Chip
              label={`Generated in ${formatElapsedTime(elapsedTime)}`}
              size="small"
              color="success"
              variant="outlined"
            />
          </Box>
        )}
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleDownload}
          startIcon={<DownloadIcon />}
        >
          Download Document
        </Button>
        <Button
          variant="outlined"
          onClick={handleGenerateAnother}
          startIcon={<RefreshIcon />}
        >
          Generate Another Version
        </Button>
        <Button
          variant="text"
          onClick={() => navigate(`/deliverables/${deliverableId}`)}
          startIcon={<ArrowBackIcon />}
        >
          Return to Deliverable
        </Button>
      </Box>
    </Box>
  );

  // Render Error View
  const renderErrorView = () => (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <ErrorIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          {operationStatus?.status === 'cancelled'
            ? 'Generation Cancelled'
            : 'Generation Failed'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {operationStatus?.status === 'cancelled'
            ? 'The document generation was cancelled.'
            : 'There was an error generating your document.'}
        </Typography>
      </Box>

      {/* Error Details */}
      {operationStatus?.error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2">{operationStatus.error}</Typography>
        </Alert>
      )}

      {operationStatus?.errorDetails && (
        <Paper variant="outlined" sx={{ mb: 3 }}>
          <Button
            fullWidth
            onClick={() => setShowErrorDetails(!showErrorDetails)}
            endIcon={showErrorDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ justifyContent: 'space-between', p: 2 }}
          >
            View Error Details
          </Button>
          <Collapse in={showErrorDetails}>
            <Box sx={{ p: 2, bgcolor: 'grey.100', fontFamily: 'monospace', fontSize: 12 }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {operationStatus.errorDetails}
              </pre>
            </Box>
          </Collapse>
        </Paper>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerateAnother}
          startIcon={<RefreshIcon />}
        >
          Retry
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate(`/deliverables/${deliverableId}`)}
          startIcon={<ArrowBackIcon />}
        >
          Return to Deliverable
        </Button>
        <Button
          variant="text"
          href="mailto:support@horwathhtl.com?subject=Document%20Generation%20Error"
          startIcon={<SupportIcon />}
        >
          Contact Support
        </Button>
      </Box>
    </Box>
  );

  // Render based on current view state
  const renderContent = () => {
    switch (viewState) {
      case 'pre-generation':
        return renderPreGenerationView();
      case 'generating':
        return renderGeneratingView();
      case 'success':
        return renderSuccessView();
      case 'error':
        return renderErrorView();
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {renderContent()}
    </Box>
  );
};

export default GenerateStep;
